import type {
  AgendaItem,
  AgendaItemMotion,
  AgendaItemUserVote,
  AgendaItemVotes,
  AgendaModule,
  Attendee,
  Document,
  Meeting,
  MinutesOrdering,
  MotionTypeMap,
  ReviewStatus,
  Team,
  TeamMember,
  Tenant,
  User,
} from "@repo/types";

// PDF Generator Configuration

import { concatIf, formatTZ } from "@repo/shared/utils";
import { Fragment, type SVGProps, useMemo } from "react";
import {
  getMeetingMinutesData,
  type MeetingMinutesData,
} from "./MeetingMinutesData";
import { multiline } from "../utilities/multiline";

// #region Icons

const makeIcon = (pathData: string) => (props: SVGProps<SVGSVGElement>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={16}
    height={16}
    {...props}
    viewBox="0 0 72 72"
  >
    <path fill="black" d={pathData} />
  </svg>
);

const IconAttachment = makeIcon(
  "M56.231,64.856c-2.983,0-5.966-1.136-8.236-3.406L21.616,35.071c-1.395-1.395-2.163-3.248-2.163-5.22s0.769-3.825,2.164-5.219c1.393-1.394,3.247-2.163,5.22-2.163s3.827,0.769,5.221,2.164l22.846,22.846c0.977,0.977,0.977,2.559,0,3.535s-2.559,0.977-3.535,0L28.521,28.168c-0.9-0.9-2.47-0.9-3.37,0c-0.451,0.451-0.699,1.048-0.699,1.684c0,0.636,0.248,1.234,0.698,1.685L51.53,57.915c2.593,2.592,6.811,2.591,9.401,0c2.592-2.592,2.592-6.81,0-9.401l-33.181-33.18c-4.255-4.254-11.178-4.254-15.433,0c-2.061,2.061-3.196,4.801-3.196,7.716c0,2.914,1.135,5.654,3.196,7.715l25.974,25.974c0.977,0.977,0.977,2.559,0,3.535s-2.56,0.977-3.535,0L8.783,34.301c-3.006-3.005-4.661-7-4.661-11.25s1.655-8.246,4.661-11.251c6.205-6.204,16.3-6.204,22.503,0l33.181,33.18c4.542,4.541,4.542,11.931,0,16.472C62.196,63.721,59.214,64.856,56.231,64.856z"
);
const IconConfidential = makeIcon(
  "M55.214,31.448h-3.506v-7.957c0-8.661-7.045-15.707-15.707-15.707c-8.659,0-15.704,7.046-15.704,15.707v7.957h-3.508c-2.096,0-3.795,1.699-3.795,3.794v25.18c0,2.096,1.699,3.795,3.795,3.795h38.425c2.094,0,3.793-1.699,3.793-3.795v-25.18C59.007,33.147,57.308,31.448,55.214,31.448z M36,55.486c-2.41,0-4.367-1.959-4.367-4.369c0-1.686,0.967-3.133,2.367-3.86v-4.194c0-1.104,0.896-2,2-2s2,0.896,2,2v4.193c1.4,0.729,2.366,2.176,2.366,3.861C40.366,53.527,38.413,55.486,36,55.486z M44.12,31.448H27.885v-7.957c0-4.477,3.643-8.119,8.116-8.119c4.477,0,8.119,3.643,8.119,8.119V31.448z"
);
const IconBrokenLink = makeIcon(
  "M39.909,43.598c1.095,1.096,1.095,2.873,0,3.968l-10.25,10.25c-2.066,2.065-4.815,3.202-7.736,3.202c0,0,0,0-0.004,0c-2.921,0-5.669-1.137-7.733-3.202c-2.071-2.065-3.21-4.813-3.206-7.739c0-2.922,1.138-5.668,3.206-7.74l10.248-10.248c1.095-1.095,2.871-1.095,3.966,0s1.095,2.869,0,3.964l-10.25,10.25c-1.008,1.01-1.564,2.349-1.564,3.773c0,1.424,0.556,2.767,1.564,3.773c2.079,2.082,5.465,2.082,7.545,0l10.25-10.252C37.041,42.502,38.814,42.502,39.909,43.598z M21.834,24.805c0.409,0.411,0.947,0.619,1.486,0.619c0.54,0,1.076-0.208,1.487-0.619c0.821-0.821,0.821-2.151,0-2.974l-5.131-5.124c-0.821-0.823-2.152-0.823-2.973,0c-0.821,0.821-0.821,2.151,0,2.974L21.834,24.805z M28.23,23.39L28.23,23.39c1.16-0.004,2.102-0.945,2.102-2.105l-0.005-7.25c0-1.162-0.942-2.104-2.102-2.104l0,0c-1.162,0-2.104,0.942-2.104,2.104l0.005,7.255C26.126,22.45,27.068,23.39,28.23,23.39z M23.39,28.227c0-1.16-0.942-2.102-2.104-2.102h-7.251c-1.164,0-2.104,0.942-2.104,2.102c0,1.164,0.942,2.102,2.104,2.102h7.251C22.448,30.329,23.39,29.387,23.39,28.227z M50.168,47.195c-0.82-0.82-2.152-0.82-2.973,0c-0.821,0.821-0.821,2.152,0,2.974l5.125,5.128c0.41,0.411,0.949,0.617,1.487,0.617c0.536,0,1.075-0.206,1.487-0.617c0.82-0.821,0.82-2.152,0-2.974L50.168,47.195z M57.969,41.671h-7.256l0,0c-1.16,0-2.102,0.938-2.102,2.101c0,1.163,0.941,2.105,2.102,2.105h7.25c0.004,0,0.004,0,0.004,0c1.16,0,2.102-0.94,2.102-2.101C60.068,42.613,59.131,41.671,57.969,41.671z M43.775,48.611L43.775,48.611c-1.162,0-2.104,0.939-2.104,2.099l-0.006,7.253c0,1.164,0.942,2.105,2.104,2.105l0,0c1.16,0,2.103-0.938,2.103-2.1l0.005-7.253C45.878,49.554,44.936,48.611,43.775,48.611z M36.054,28.4l10.25-10.25c2.082-2.08,5.466-2.08,7.546,0c2.078,2.081,2.078,5.467,0,7.545l-10.248,10.25c-1.096,1.095-1.096,2.869,0,3.964c0.546,0.549,1.264,0.82,1.98,0.82c0.719,0,1.435-0.271,1.982-0.82l10.25-10.25c4.266-4.265,4.266-11.206,0-15.477c-4.271-4.266-11.211-4.264-15.475,0.003L32.09,24.435c-1.095,1.095-1.095,2.871,0,3.966S34.96,29.496,36.054,28.4z"
  //"M14.665,17.95c-0.909-0.906-0.909-2.38,0-3.286c0.907-0.909,2.38-0.909,3.287,0l5.675,5.671c0.909,0.906,0.909,2.38,0,3.286c-0.455,0.455-1.05,0.683-1.645,0.683s-1.189-0.228-1.645-0.683L14.665,17.95z M27.407,22.054h0.002c1.284-0.002,2.325-1.043,2.325-2.327l-0.007-8.018c-0.002-1.284-1.043-2.322-2.324-2.322H27.4c-1.284,0-2.324,1.042-2.324,2.327l0.006,8.018C25.085,21.013,26.125,22.054,27.407,22.054z M11.712,29.733h8.018c1.285,0,2.325-1.043,2.325-2.327c0-1.282-1.04-2.325-2.325-2.325h-8.018c-1.284,0-2.324,1.043-2.324,2.325C9.388,28.69,10.428,29.733,11.712,29.733z M51.669,48.381c-0.905-0.906-2.376-0.911-3.287,0c-0.909,0.904-0.909,2.38,0,3.286l5.666,5.676c0.455,0.454,1.05,0.681,1.645,0.681s1.19-0.227,1.645-0.681c0.909-0.905,0.909-2.38,0-3.289L51.669,48.381z M60.296,42.273l-8.023-0.002c-1.284,0-2.325,1.04-2.325,2.324s1.041,2.325,2.325,2.325l8.023,0.002c1.282,0,2.325-1.04,2.325-2.324C62.621,43.313,61.578,42.273,60.296,42.273z M44.604,49.946c-0.004,0-0.004,0-0.004,0c-1.284,0-2.325,1.038-2.325,2.322l-0.007,8.022c0,1.286,1.039,2.329,2.323,2.329c0.002,0,0.002,0,0.002,0c1.284,0,2.327-1.038,2.327-2.322l0.004-8.024C46.924,50.986,45.886,49.946,44.604,49.946z M36.099,39.463c-1.659,0.424-2.661,2.114-2.234,3.774c0.63,2.461-0.04,4.97-1.794,6.72l-5.152,5.155c-1.34,1.337-3.12,2.076-5.017,2.076c-1.895,0-3.673-0.737-5.013-2.081c-1.341-1.335-2.078-3.115-2.076-5.01c0-1.893,0.737-3.678,2.076-5.015l5.157-5.156c1.748-1.752,4.259-2.415,6.718-1.79c1.662,0.433,3.349-0.572,3.774-2.232c0.424-1.659-0.575-3.348-2.233-3.773c-4.614-1.184-9.339,0.097-12.645,3.416l-5.155,5.152c-2.51,2.51-3.893,5.847-3.893,9.398c-0.002,3.55,1.381,6.892,3.893,9.398c2.507,2.51,5.844,3.893,9.396,3.893c0,0,0.002,0,0.004,0c3.548,0,6.888-1.383,9.396-3.893l5.15-5.15c3.321-3.313,4.6-8.039,3.418-12.647C39.446,40.036,37.756,39.033,36.099,39.463z M54.343,36.455l5.154-5.152c5.184-5.186,5.184-13.62,0-18.799c-2.507-2.51-5.849-3.893-9.398-3.893c-3.552,0-6.889,1.383-9.398,3.893l-5.152,5.152c-3.317,3.313-4.595,8.042-3.416,12.647c0.424,1.658,2.105,2.663,3.771,2.235c1.657-0.424,2.658-2.114,2.235-3.771c-0.631-2.461,0.041-4.975,1.793-6.725l5.153-5.155c2.676-2.676,7.35-2.681,10.028,0.003c2.764,2.762,2.764,7.262,0,10.028l-5.157,5.154c-1.747,1.75-4.257,2.42-6.718,1.789c-1.651-0.435-3.351,0.575-3.774,2.235c-0.423,1.657,0.575,3.348,2.235,3.771c1.129,0.288,2.266,0.431,3.388,0.431C48.538,40.3,51.844,38.958,54.343,36.455z"
);

// #endregion

// #region Components

const userName = (
  user: string | undefined,
  userMap: Record<string, User>
): React.ReactNode => {
  const userObj = userMap[user ?? ""];
  return userObj ? (
    `${userObj.firstName} ${userObj.lastName}`
  ) : user === "none" ? (
    "Nobody"
  ) : user === "null" ? (
    "N/A"
  ) : (
    <span style={{ backgroundColor: "#ff9900" }}>&lt;Unknown&gt;</span>
  );
};

const Hr = () => <hr style={{ marginTop: 10, marginBottom: 10 }} />;

// #endregion

// #region C: Meeting Hdr

interface MeetingDetailsBlockProps {
  meeting: Meeting | undefined;
  timeZone: string;
  enableLinks: boolean;
}
/** Meeting details heading */
const MeetingDetailsBlock = ({
  meeting,
  timeZone,
}: MeetingDetailsBlockProps) => (
  <>
    <h1 style={{ textAlign: "center" }}>
      {meeting ? meeting.name : "{Meeting Name}"} Minutes
    </h1>

    <p style={{ textAlign: "center" }}>
      {meeting
        ? formatTZ(meeting.startTime, "MMMM dd, yyyy", timeZone)
        : "MMMM dd, yyyy"}
    </p>

    <p style={{ textAlign: "center" }}>
      Meeting Start:{" "}
      {meeting ? formatTZ(meeting.startTime, "h:mm a", timeZone) : "h:mm a"}
    </p>

    {meeting?.description ? (
      <p style={{ textAlign: "center" }}>{meeting.description}</p>
    ) : null}
  </>
);

// #endregion

// const indentSize = 20;
const indentClass = (depth: number) =>
  depth ? "ql-indent-" + Math.min(depth, 8) : "";

// #region C: Document

interface DocumentBlockProps {
  document: Document;
  enableLinks: boolean;
}
/** Render document */
const DocumentBlock = ({ document: doc, enableLinks }: DocumentBlockProps) => {
  const attachment = doc.linkedAttachment
    ? doc.attachments.find(
        (attachment) => attachment.attachmentId === doc.linkedAttachment
      )
    : undefined;

  return doc.visibleOnAgenda ?? true ? (
    <p>
      {doc.confidential ? (
        <IconConfidential style={{ display: "inline-block", marginRight: 4 }} />
      ) : !attachment?.url ? (
        <IconBrokenLink style={{ display: "inline-block", marginRight: 4 }} />
      ) : (
        <IconAttachment style={{ display: "inline-block", marginRight: 4 }} />
      )}

      {doc.confidential || !attachment?.url ? (
        doc.name
      ) : (
        <a href={enableLinks ? attachment.url : "#"}>{doc.name}</a>
      )}
    </p>
  ) : null;
};

// #endregion

// #region C: Minute

/** Render  */
interface MinuteProps {
  time: undefined | false | Date;
  timeZone: string;
}
const Minute = ({ time, timeZone }: MinuteProps) =>
  !!time && (
    <span className="ql-size-small" style={{ color: "#bbbbbb" }}>
      {formatTZ(time, "h:mm aa \xa0 ", timeZone)}
    </span>
  );

// #endregion

// #region C: Votes Line

const voteTypeLabel: Record<AgendaItemVotes["type"], string> = {
  hand: "by hand",
  rollcall: "by roll call",
  voice: "by voice",
  ballot: "by ballot",
};
const voteLabels: Record<AgendaItemUserVote["vote"], string> = {
  yes: "yes",
  no: "no",
  abstain: "abstained",
};

interface VotesLineProps {
  votes?: AgendaItemVotes;
  status: ReviewStatus;
  userMap: Record<string, User>;
  depth: number;
  timestamp?: number;
  timeZone: string;
}
/** 'Who voted what' line */
const VotesLine = ({
  votes,
  status,
  userMap,
  depth,
  timestamp,
  timeZone,
}: VotesLineProps) => {
  const time = !!timestamp && new Date(timestamp);

  const unanimous =
    votes?.users?.length && votes.users.every((user) => user.vote === "yes");

  return (
    <>
      {!unanimous && !!votes?.users?.length && (
        <p className={indentClass(depth)}>
          The following votes were taken {voteTypeLabel[votes.type]}:{" "}
          {votes.users.map((vote, i) => (
            <Fragment key={vote.userId}>
              {i > 0 ? "; " : ""}
              {userName(vote.userId, userMap)} {voteLabels[vote.vote]}
              {vote.reason ? <> ({vote.reason})</> : ""}
            </Fragment>
          ))}
          .
        </p>
      )}
      {status === "approved" ? (
        <p className={indentClass(depth)}>
          <Minute time={time} timeZone={timeZone} />

          <span style={{ fontStyle: "italic" }}>
            The motion{" "}
            <span style={{ textDecoration: "underline" }}>
              carried{unanimous ? " unanimously" : ""}
            </span>
            .
          </span>
        </p>
      ) : status === "rejected" ? (
        <p className={indentClass(depth)}>
          <Minute time={time} timeZone={timeZone} />

          <span style={{ fontStyle: "italic" }}>
            The motion{" "}
            <span style={{ textDecoration: "underline" }}>failed</span>.
          </span>
        </p>
      ) : null}
    </>
  );
};

// #endregion

const statusOrder: Attendee["status"][] = ["present", "phone", "absent"];
const statusLabel: Record<Attendee["status"], string> = {
  present: "all present",
  phone: "present (remotely)",
  absent: "absent",
};

const isAgendaModule = (
  motion: MeetingMinutesData[0]
): motion is AgendaModule => "id" in motion && "agendaItemIds" in motion;

const isTeamMember = (motion: MeetingMinutesData[0]): motion is TeamMember =>
  !("id" in motion) && "userId" in motion;

const isMeetingMotion = (
  motion: MeetingMinutesData[0],
  motionTypes: MotionTypeMap
) =>
  !("id" in motion) &&
  !("userId" in motion) &&
  motion.motionId in motionTypes.meeting;

// #region C: Opening

interface MeetingOpeningProps {
  meeting: Meeting;
  team: Team;
  userMap: Record<string, User>;
  timeZone: string;
}

const MeetingOpening = ({
  meeting,
  team,
  userMap,
  timeZone,
}: MeetingOpeningProps) => {
  const user =
    userMap[meeting.review?.chairman ?? meeting.review?.secretary ?? ""];
  const startTime = meeting.review?.startTime;
  const address = meeting.address ?? team.address;
  const addressString = address
    ? concatIf(
        address.street,
        " ",
        address.street2,
        " ",
        address.city,
        ", ",
        address.state || "PA",
        " ",
        address.zip,
        " ",
        address.country
      )
    : null;

  return (
    <p>
      The meeting was called to order
      {user ? (
        <>
          {" "}
          by {user.firstName} {user.lastName}
        </>
      ) : null}
      {startTime ? (
        <>
          {" "}
          at {formatTZ(startTime, "h:mm a", timeZone)} on{" "}
          {formatTZ(startTime, "MMMM dd, yyyy", timeZone)}
        </>
      ) : null}
      {addressString ? <> at {addressString}</> : null}.
    </p>
  );
};

// #endregion

// #region C: Roll Call

interface MeetingMinutesRollCallProps {
  meeting: Meeting;
  userMap: Record<string, User>;
}
/** Meeting minutes roll call */
const MeetingMinutesRollCall = ({
  meeting,
  userMap,
}: MeetingMinutesRollCallProps) => {
  const attendees = (meeting.review?.attendees ?? []).reduce(
    (map, attendee) => {
      (map[attendee.status] ??= []).push(attendee);
      return map;
    },
    Object.create(null) as Record<Attendee["status"], Attendee[]>
  );

  return (
    <>
      <p style={{ fontWeight: "bold", textDecoration: "underline" }}>
        Roll Call
      </p>
      <p>
        Members present included{" "}
        {statusOrder.map((status, i) => {
          if (!attendees[status]) return null;
          return (
            <Fragment key={status}>
              {i > 0 ? " " : ""}
              {attendees[status].map((attendee, i) => (
                <Fragment key={attendee.userId}>
                  {i > 0 ? ", " : ""}
                  {userName(attendee.userId, userMap)}
                </Fragment>
              ))}
              ; {statusLabel[status]}.
            </Fragment>
          );
        })}
      </p>
    </>
  );
};

// #endregion

// #region C: Minute Block

interface MeetingMinutesBlockProps {
  minutes: MeetingMinutesData;
  motionTypes: MotionTypeMap;
  enableLinks: boolean;
  userMap: Record<string, User>;
  team: Team;
  timeZone: string;
  depth: number;
}
const MeetingMinutesBlock = ({
  minutes,
  motionTypes,
  enableLinks,
  userMap,
  team,
  timeZone,
  depth,
}: MeetingMinutesBlockProps) => (
  <>
    {minutes.map((item, index, array) => {
      if (isAgendaModule(item)) {
        return (
          <Fragment key={"section:" + item.id}>
            <h3>
              <strong>{item.name}</strong>
            </h3>
            {item.description ? <p>{multiline(item.description)}</p> : null}
          </Fragment>
        );
      }
      if (isTeamMember(item)) {
        return (
          <p key={"user:" + item.userId}>
            <br />
            <br />
            Respectfully submitted by:
            <br />
            {userName(item.userId, userMap)}
            <br />
            {item.title}
          </p>
        );
      }
      return (
        <Fragment key={"id" in item ? item.id : index}>
          {/* if this is the first item and nested, or if it's not the first, but the previous
            is not a meeting motion, and if this is a meeting motion, then insert <hr> */}
          {((depth > 0 && !array[index - 1]) ||
            (array[index - 1] &&
              !isMeetingMotion(array[index - 1], motionTypes))) &&
            isMeetingMotion(item, motionTypes) && <Hr />}

          {"id" in item ? (
            <MeetingItemMotionBlock
              key={"item:" + item.id}
              motion={item}
              motionTypes={motionTypes}
              enableLinks={enableLinks}
              userMap={userMap}
              team={team}
              timeZone={timeZone}
              depth={depth}
            />
          ) : (
            <MeetingMotionBlock
              key={"motion:" + index}
              motion={item}
              motionTypes={motionTypes}
              userMap={userMap}
              enableLinks={enableLinks}
              team={team}
              timeZone={timeZone}
              depth={depth}
            />
          )}

          {/* if this is the last item and nested, or if it's not the last, but the next
            is not a meeting motion, and if this is a meeting motion, then insert <hr> */}
          {((depth > 0 && !array[index + 1]) ||
            (array[index + 1] &&
              !isMeetingMotion(array[index + 1], motionTypes))) &&
            isMeetingMotion(item, motionTypes) && <Hr />}
        </Fragment>
      );
    })}
  </>
);

// #endregion

// #region C: Item Motion

interface MeetingItemMotionBlockProps {
  motion: AgendaItem;
  motionTypes: MotionTypeMap;
  team: Team;
  enableLinks: boolean;
  userMap: Record<string, User>;
  timeZone: string;
  depth: number;
}
const MeetingItemMotionBlock = ({
  motion,
  motionTypes,
  team,
  enableLinks,
  userMap,
  timeZone,
  depth,
}: MeetingItemMotionBlockProps) => {
  const presenter = motion.presenter
    ? typeof motion.presenter === "string" // upgrade old version
      ? { userId: motion.presenter }
      : motion.presenter
    : { userId: "none" };

  const presenterSet =
    presenter.userId !== "none" &&
    !!(presenter.userId || presenter.manualEntry);

  const time = !!motion.review?.timestamp && new Date(motion.review?.timestamp);

  const movedBy = userName(motion.review?.moved, userMap);
  const secondedBy = userName(motion.review?.seconded, userMap);

  const indentCls = indentClass(depth);

  const motionText = motion.review?.originalMotion ?? motion.motion;

  return (
    <>
      <p className={indentCls}>
        <Minute time={time} timeZone={timeZone} />

        <span style={{ fontWeight: "bold", textDecoration: "underline" }}>
          {motion.name}
        </span>
      </p>

      {!!motion.documents?.length &&
        motion.documents.map((document) => (
          <DocumentBlock
            key={document.id}
            document={document}
            enableLinks={enableLinks}
          />
        ))}

      {presenterSet ? (
        <p className={indentCls}>
          {presenter.userId
            ? userName(presenter.userId, userMap)
            : presenter.manualEntry}{" "}
          presented this motion
          {motion.description ? ": " + motion.description : "."}
        </p>
      ) : (
        motion.description
      )}

      {motion.approver?.approverType === "team" && (
        <p className={indentCls}>
          A motion was made by {movedBy}, seconded by {secondedBy}
          {motionText ? ", to " + motionText : ""}.
        </p>
      )}

      {!!motion.review?.comments && (
        <>
          <p className={indentCls}>The following discussion ensued:</p>
          <p style={{ fontStyle: "italic" }} className={indentCls}>
            {multiline(motion.review.comments)}
          </p>
        </>
      )}

      {!!motion.review?.motions?.length && (
        <MeetingMinutesBlock
          minutes={motion.review.motions}
          motionTypes={motionTypes}
          team={team}
          userMap={userMap}
          enableLinks={enableLinks}
          timeZone={timeZone}
          depth={depth + 1}
        />
      )}

      {motion.approver?.approverType === "team" &&
        (motion.review?.status && motion.review.status !== "pending" ? (
          <VotesLine
            votes={motion.review?.votes}
            status={motion.review?.status ?? "pending"}
            userMap={userMap}
            depth={depth}
            timestamp={motion.review?.timestampEnd}
            timeZone={timeZone}
          />
        ) : (
          <p className={indentCls}>No action was taken on the item.</p>
        ))}

      {motion.review?.status === "deferred" && (
        <p className={indentCls}>
          The motion was deferred to{" "}
          {motion.requestedApprovalDate
            ? formatTZ(motion.requestedApprovalDate, "MM/dd/yyyy", timeZone)
            : "a later time"}
          .
        </p>
      )}
    </>
  );
};

// #endregion

// #region C: Motion

const motionNameOverrides: Record<string, string> = {
  amend: "Amend the motion to:",
};

interface MeetingMotionBlockProps {
  motion: AgendaItemMotion;
  motionTypes: MotionTypeMap;
  userMap: Record<string, User>;
  enableLinks: boolean;
  team: Team;
  timeZone: string;
  depth: number;
}
const MeetingMotionBlock = ({
  motion,
  motionTypes,
  userMap,
  enableLinks,
  team,
  timeZone,
  depth,
}: MeetingMotionBlockProps) => {
  const motionType =
    motionTypes.item[motion.motionId] ?? motionTypes.meeting[motion.motionId];
  const meetingMotion = isMeetingMotion(motion, motionTypes);

  const movedBy = userName(motion.moved, userMap);
  const secondedBy = motionType.second
    ? userName(motion.seconded, userMap)
    : "";

  const time = new Date(motion.timestamp);

  const indentCls = indentClass(meetingMotion ? 0 : depth);

  return (
    <>
      <p className={indentCls}>
        <Minute time={time} timeZone={timeZone} />

        <span style={{ fontStyle: "italic", textDecoration: "underline" }}>
          A motion was made by {movedBy}
          {motionType.second ? <>, seconded by {secondedBy},</> : null} to{" "}
          {motionNameOverrides[motionType.id] ?? motionType.name}
        </span>
      </p>

      {!!motionType.amend && (
        <p style={{ fontStyle: "italic" }} className={indentCls}>
          "{motion.amendment}"
        </p>
      )}

      {motionType.debate && !!motion.debate && (
        <>
          <p className={indentCls}>The following discussion ensued:</p>
          <p style={{ fontStyle: "italic" }} className={indentCls}>
            {multiline(motion.debate)}
          </p>
        </>
      )}

      {!!motion.nesting?.length && (
        <>
          <p className={indentCls}>
            During which, the following motions were made:
          </p>

          <MeetingMinutesBlock
            minutes={motion.nesting}
            motionTypes={motionTypes}
            team={team}
            userMap={userMap}
            enableLinks={enableLinks}
            timeZone={timeZone}
            depth={depth + 1}
          />
        </>
      )}

      {!!motionType.voting && !!motion.votes && (
        <VotesLine
          votes={motion.votes}
          status={motion.status ?? "pending"}
          userMap={userMap}
          depth={meetingMotion ? 0 : depth}
          timestamp={motion.timestampEnd}
          timeZone={timeZone}
        />
      )}
    </>
  );
};

// #endregion

// #region Document Root

export interface MeetingMinutesHTMLProps {
  tenant?: Tenant;
  meeting?: Meeting;
  agendaItemMap?: Record<string, AgendaItem>;
  enableLinks?: boolean;
  motionTypes: MotionTypeMap;
  userMap: Record<string, User>;
  team: Team;
  submitter?: TeamMember;
  order?: MinutesOrdering;
}

export const MeetingMinutesHTML = ({
  tenant,
  meeting,
  agendaItemMap = {},
  enableLinks = true,
  motionTypes,
  userMap,
  team,
  submitter,
  order,
}: MeetingMinutesHTMLProps) => {
  // extract and sort meeting minutes from Meeting
  const minutes = useMemo(
    () => getMeetingMinutesData({ meeting, agendaItemMap, submitter, order }),
    [meeting, agendaItemMap, submitter, order]
  );

  const timeZone = tenant?.timeZone ?? "America/New_York";

  return (
    <>
      {/* Meeting minutes details */}
      <MeetingDetailsBlock
        meeting={meeting}
        enableLinks={enableLinks}
        timeZone={timeZone}
      />

      {!!meeting && (
        <MeetingOpening
          meeting={meeting}
          team={team}
          userMap={userMap}
          timeZone={timeZone}
        />
      )}

      {!!meeting && (
        <MeetingMinutesRollCall meeting={meeting} userMap={userMap} />
      )}

      <MeetingMinutesBlock
        minutes={minutes}
        motionTypes={motionTypes}
        enableLinks={enableLinks}
        userMap={userMap}
        team={team}
        timeZone={timeZone}
        depth={0}
      />
    </>
  );
};

// #endregion
